/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injectable, inject } from '@angular/core';

import { DisplayType, Fixture, FixtureStage, FixtureViewType, OfferCategory, OptionMarket, Participant } from '@cds/betting-offer';
import { BetBuilderProvider, StreamProvider, StreamType } from '@cds/betting-offer/add-ons';
import { Rank } from '@cds/betting-offer/add-ons/statistics';
import { BetBuilderOptionGroupInfo } from '@cds/betting-offer/domain-specific/bet-builder';
import { ScoreboardSlim } from '@cds/scoreboards/v1';
import {
    BetBuilderInfo,
    EventModel,
    EventParticipant,
    EventParticipantType,
    EventStream,
    FeedType,
    RegionModel,
    Tv2MarketParameters,
    getProvider,
} from '@frontend/sports/betting-offer/feature/model';
import {
    BetBuilderConfig,
    ImgConfigurationData,
    MarketGroupingConfig,
    MediaConfig,
    OverrideStatisticsConfig,
} from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { MarketTypeWithDisplayName } from '@frontend/sports/types/configuration';
import { UrlService } from '@frontend/vanilla/core';
import { clone } from 'lodash-es';

import { isPlayer, mapLeague, mapParticipants, mapSport, mapToEventParticipants } from '../helpers/event-mapper';
import { ScoreboardFactory } from './scoreboard-factory.service';

@Injectable({ providedIn: 'root' })
export class CommonFixtureFactory {
    private mediaConfig = inject(MediaConfig);
    private imgConfig = inject(ImgConfigurationData);
    private scoreboardFactory = inject(ScoreboardFactory);
    private marketGroupingConfig = inject(MarketGroupingConfig);
    private overrideStatisticsConfig = inject(OverrideStatisticsConfig);
    private betBuilderConfig = inject(BetBuilderConfig);
    private urlService = inject(UrlService);

    //TODO please refactor when you are modifying this function, and remove the above eslint rule bypass, this function is awful!
    // eslint-disable-next-line max-lines-per-function
    create(fixture: Fixture, sourceId?: string, precreatedOptionGroupInfo?: BetBuilderOptionGroupInfo[]): EventModel {
        const model = new EventModel();

        model.id = sourceId ?? fixture.id;

        model.name = fixture.name.short || fixture.name.value;
        model.nameSign = fixture.name.short ? fixture.name.shortSign : fixture.name.sign;
        model.description = fixture.description?.value;
        model.sport = mapSport(fixture);
        model.region = this.mapRegion(fixture);

        model.league = mapLeague(fixture.competition, fixture.tradingStage?.id || fixture.group?.parentId, fixture.group?.id);

        model.startDate = new Date(fixture.startDate);
        model.cutOffDate = new Date(fixture.cutOffDate);
        model.live = fixture.stage === FixtureStage.Live;
        model.groupId = fixture.groupId;
        model.liveAlert = fixture.liveAlert;
        model.betBuilderFixtureId = fixture.addons?.betBuilderFixture;
        model.sportcastId = fixture.addons?.sportcastId;
        model.betBuilderTradingV2FixtureId = fixture.addons?.betBuilderTradingV2FixtureId;
        model.online = fixture.isOpenForBetting;

        model.runningBallDataId = this.setMatchSimulation(fixture);
        model.participants = mapParticipants(
            fixture.participants.filter((p) => !isPlayer(p)),
            fixture.viewType === FixtureViewType.NotSet ? FixtureViewType.European : fixture.viewType,
            fixture.hybridFixtureData,
        );
        model.teams = this.mapTeams(
            fixture.participants.filter((p) => !isPlayer(p)),
            fixture.viewType === FixtureViewType.NotSet ? FixtureViewType.European : fixture.viewType,
        );
        model.players = this.mapPlayers(fixture.participants.filter((p) => isPlayer(p)));

        model.gamesCount = this.getMarketsCount(fixture);

        model.offerSource = fixture.source;
        model.offerContext = fixture.contexts;
        model.fixtureType = fixture.fixtureType;
        model.hasLeagueStats = this.hasLeagueStatistics(fixture);
        model.competitionGroupId = fixture.group?.id;
        model.rank = this.getRanks(fixture.sport.id, fixture.competition.id, model.participants);

        if (fixture.addons) {
            model.surface = fixture.addons.surface;
            model.statsId = fixture.addons.betRadar!;
            model.hasFixtureStats = fixture.addons.statistics!;
            model.provider = getProvider(fixture.addons);
            model.fixtureForm = fixture.addons.fixtureStatistics?.fixtureForm;
            model.seasonStandings = fixture.addons.fixtureStatistics?.seasonStandings;
            model.startingPitcher = fixture.addons.fixtureStatistics?.startingPitcher;
            model.stream = this.getStream(fixture);

            if (fixture.addons.imgEventId) {
                model.imgEventId = this.setImgAnimation(fixture);
            }
            if (fixture.addons.liveMatchTracker) {
                model.betRadarVisualizationId = fixture.addons.liveMatchTracker.sportradarId;
                model.betRadarCoverageLevel = fixture.addons.liveMatchTracker.coverageLevel;
            }
        }
        model.viewType = fixture.viewType;
        model.scoreboard = this.scoreboardFactory.createScoreboard(fixture.scoreboard as ScoreboardSlim, model);
        model.totalMarketsCount = fixture.totalMarketsCount;
        model.priceBoostCount = fixture.priceBoostCount;
        model.hybridFixtureData = fixture.hybridFixtureData;
        model.offerCategories = this.getOfferCategories(fixture);
        model.betBuilderInfo = this.mapBetBuilderInfo(fixture, model);

        if (precreatedOptionGroupInfo && precreatedOptionGroupInfo.length > 0) {
            model.precreatedOptionGroups = precreatedOptionGroupInfo;
        }

        return model;
    }

    update(model: EventModel, openForBetting: boolean, stage: FixtureStage): void {
        // eslint-disable-next-line no-param-reassign
        model.live = stage === FixtureStage.Live;

        if (model.online === openForBetting) {
            return;
        }

        // eslint-disable-next-line no-param-reassign
        model.online = openForBetting;
        model.optionGroups.forEach((optionGroup) => {
            // eslint-disable-next-line no-param-reassign
            optionGroup.options.forEach((option) => (option.online = openForBetting && option.visible));
            // eslint-disable-next-line no-param-reassign
            optionGroup.online = openForBetting && optionGroup.visible;
        });
    }

    updateScoreboard(serverScoreboard: ScoreboardSlim, model: EventModel): void {
        // eslint-disable-next-line no-param-reassign
        model.scoreboard = this.scoreboardFactory.createScoreboard(serverScoreboard, model);
    }

    private getMarketsCount(fixture: Fixture): number {
        // eslint-disable-next-line unicorn/explicit-length-check
        let count = (fixture.games && fixture.games.length) || 0;
        // eslint-disable-next-line unicorn/explicit-length-check
        count += (fixture.optionMarkets && fixture.optionMarkets.length) || 0;

        return count;
    }

    private getOfferCategories(fixture: Fixture): OfferCategory[] {
        return [
            ...new Set([
                ...fixture.games.map((game) => game.category),
                ...fixture.optionMarkets.reduce((result, o) => {
                    if (o.templateCategory) {
                        result.push(o.templateCategory.category);
                    }

                    return result;
                }, [] as OfferCategory[]),
            ]),
        ];
    }

    private setImgAnimation(fixture: Fixture): number | undefined {
        return fixture.sport.id === SportConstant.Golf &&
            this.imgConfig.isEnabled &&
            !this.imgConfig.blacklistedCompetitions.includes(fixture.competition.id)
            ? fixture.addons.imgEventId
            : undefined;
    }

    private setMatchSimulation(fixture: Fixture): string | undefined {
        if (
            !this.mediaConfig.isMatchSimulationEnabled ||
            !this.mediaConfig.sportsWithMatchSimulation ||
            !this.mediaConfig.sportsWithMatchSimulation.length
        ) {
            return undefined;
        }

        return this.mediaConfig.sportsWithMatchSimulation.includes(fixture.sport.id) ? fixture.addons && fixture.addons.runningBall : undefined;
    }

    private mapRegion(fixture: Fixture): RegionModel {
        const source = fixture.tournament || fixture.region;
        const region = new RegionModel();

        if (source) {
            region.id = source.id;
            region.code = fixture.tournament ? undefined : fixture.region.code;
            region.name = source.name.short || source.name.value;
            region.sign = source.name.short ? source.name.shortSign : source.name.sign;

            if (fixture.region) {
                region.originalRegion = {
                    id: fixture.region.id,
                    code: fixture.region.code,
                    name: fixture.region.name.short || fixture.region.name.value,
                    sign: fixture.region.name.short ? fixture.region.name.shortSign : fixture.region.name.sign,
                };
            }
        }

        return region;
    }

    private mapTeams(participants: Participant[], fixtureViewType: FixtureViewType): EventParticipant[] {
        //teams not used currently - mapping can be changed as needed
        return mapToEventParticipants(participants, fixtureViewType);
    }

    private mapPlayers(playerParticipants: Participant[]): EventParticipant[] {
        return playerParticipants.map<EventParticipant>((participant) => {
            const name = participant.name.value;

            return {
                id: participant.participantId,
                fixtureParticipantId: participant.id,
                shortName: participant.name.short,
                name,
                image: clone(participant.image),
                teamId: participant.properties!.team,
            };
        });
    }

    shouldOverrideParticipantNames(optionMarket: OptionMarket, sportId: number): boolean {
        const marketType = optionMarket.parameters?.find((x) => x.key === Tv2MarketParameters.MarketType)?.value;
        const displayTypes = optionMarket.grouping?.detailed?.map((g) => g.displayType);

        return !!(
            marketType &&
            this.marketGroupingConfig.overrideParticipantNameOptionMarkets.some(
                (config) =>
                    config.sportId === sportId &&
                    config.types.some((type) => type.marketType === marketType && this.isOverrideParticipantNamebyDisplayType(type, displayTypes)),
            )
        );
    }

    private isOverrideParticipantNamebyDisplayType(marketConfig: MarketTypeWithDisplayName, displayTypes: DisplayType[]): boolean {
        if (marketConfig.displayType && displayTypes) {
            return displayTypes.some((displayType) => displayType === marketConfig.displayType);
        }

        return true;
    }

    private getRanks(sportId: number, leagueId: number, participants: EventParticipant[]): Rank | undefined {
        const rankings = this.overrideStatisticsConfig.teamRankings;
        const [homeTeamParticipantId, awayTeamParticipantId] = this.getParticipantId(participants);
        const rankingsOnLeague = rankings?.[sportId]?.[leagueId];
        const homeTeamRank = homeTeamParticipantId ? rankingsOnLeague?.[homeTeamParticipantId] : undefined;
        const awayTeamRank = awayTeamParticipantId ? rankingsOnLeague?.[awayTeamParticipantId] : undefined;

        return homeTeamRank || awayTeamRank ? { homeTeam: homeTeamRank, awayTeam: awayTeamRank } : undefined;
    }

    private getParticipantId(participants: EventParticipant[]): [number | undefined, number | undefined] {
        const homeParticipantId = participants.find((participant) => participant.type === EventParticipantType.Home)?.id;
        const awayParticipantId = participants.find((participant) => participant.type === EventParticipantType.Away)?.id;

        return [homeParticipantId, awayParticipantId];
    }

    private mapBetBuilderInfo(fixture: Fixture, eventModel: EventModel): BetBuilderInfo | null {
        const isBetBuilderDisabled =
            !this.betBuilderConfig.isBetBuilderEnabled ||
            !this.betBuilderConfig.isBetBuilderEnabledForSport?.includes(eventModel.sport.id) ||
            fixture.addons.betBuilderProvider === BetBuilderProvider.None;

        const preMatchBuilderCase = !isBetBuilderDisabled && (fixture.stage === FixtureStage.PreMatch || fixture.stage === undefined);
        const liveBuilderCase =
            !isBetBuilderDisabled &&
            fixture.stage === FixtureStage.Live &&
            (this.betBuilderConfig.liveBetBuilderEnabledCompetitions?.includes(fixture.competition.id) ||
                this.betBuilderConfig.liveBetBuilderEnabledSports?.includes(fixture.sport.id));

        if (!preMatchBuilderCase && !liveBuilderCase) {
            return null;
        }

        return { betBuilderId: fixture.addons?.betBuilderId, betBuilderProvider: fixture.addons.betBuilderProvider };
    }

    private getStream(fixture: Fixture): EventStream | undefined {
        let stream: EventStream | undefined;
        const videoStream = fixture.addons.videoStream;

        if (this.mediaConfig.showVideoStreaming && !this.mediaConfig.restrictedSportIds.some((id) => id === fixture.sport.id) && videoStream) {
            const { provider, embedPath, feedType } = videoStream;

            if (
                provider === StreamProvider.PerformV2 &&
                !this.mediaConfig.contentPlayerProEnabledSports.includes(fixture.sport.id) &&
                videoStream.streamType === StreamType.FastData
            ) {
                return stream;
            }

            stream = {
                id: videoStream.providerStreamId,
                provider,
                feedType: FeedType[feedType as keyof typeof FeedType],
                providerEventId: videoStream.providerEventId,
                streamType: videoStream.streamType,
                outletKey: videoStream.performOutletKey,
                performUrl: videoStream.performBaseUrl,
            };

            if (provider === StreamProvider.Unikrn && embedPath) {
                if (feedType === FeedType.Twitch) {
                    stream.twitchChannel = this.urlService.parse(embedPath).search.get('channel') ?? undefined;
                } else {
                    stream.streamUrl = embedPath;
                }
            }
        }

        return stream;
    }

    private hasLeagueStatistics(fixture: Fixture): boolean {
        if (!fixture.competition?.statistics) {
            return false;
        }

        if (fixture.group && !fixture.group.statistics) {
            return false;
        }

        if (fixture.tradingStage && !fixture.tradingStage.statistics) {
            return false;
        }

        return true;
    }
}
